import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Lock, Mail, Eye, EyeOff, Loader2, User, LayoutDashboard, Phone, Globe, CheckCircle, KeyRound, ArrowLeft,
  FolderPlus, Folder, FileText, Upload, Trash2, Download, Edit2, X, Plus, Building, 
  Briefcase, FileSpreadsheet, File, ChevronRight, Search, ExternalLink, CalendarDays, 
  ListTodo, Clock, AlertCircle, CheckSquare, Square, ChevronLeft, Home, Shield, Languages, Banknote
} from 'lucide-react';
import { ComplianceApp } from './compliance';
import { MortgageApp } from './mortgage';
import CarrierAffiliations from '../components/CarrierAffiliations';
import AdvisorComplianceProfile from '../components/AdvisorComplianceProfile';
import { TranslationPanel } from '../components/TranslationPanel';
import { TeamPhoto } from '../components/TeamPhoto';
import RegulatoryFormsTab from '../components/RegulatoryFormsTab';
import AgentDocumentsTab from '../components/AgentDocumentsTab';
import RequestDocsButton, { CategoryBadge } from '../components/RequestDocsButton';
import { MergeFilesPanel } from '../components/MergeFilesPanel';
import ClientDocsBoard from '../components/ClientDocsBoard';
import AgentMemosPanel from '../components/AgentMemosPanel';
import { LinkifiedText } from '../components/LinkifiedText';
import { DraggableTabs, DragHint } from '../components/DraggableTabs';
import ResetPasswordForm from '../components/ResetPasswordForm';
import AutopilotBanner from '../components/AutopilotBanner';
import BirthdayField from '../components/BirthdayField';
import AgentDashboard from '../components/AgentDashboard';
import { getToken, setSession, clearSession, setDownloadToken, openAuthed } from '../lib/authSession';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Helper to get file icon based on type
const getFileIcon = (fileType) => {
  if (fileType?.includes('pdf')) return <FileText className="h-5 w-5 text-red-500" />;
  if (fileType?.includes('spreadsheet') || fileType?.includes('excel')) return <FileSpreadsheet className="h-5 w-5 text-green-500" />;
  if (fileType?.includes('word') || fileType?.includes('document')) return <FileText className="h-5 w-5 text-blue-500" />;
  return <File className="h-5 w-5 text-gray-500" />;
};

const AgentLoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [agent, setAgent] = useState(null);
  
  // Dashboard tab state
  const [activeTab, setActiveTab] = useState('dashboard');
  const [tabOrder, setTabOrder] = useState([]);
  
  // Forgot password states
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotResult, setForgotResult] = useState(null);
  
  // Change password states
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changeLoading, setChangeLoading] = useState(false);
  const [changeError, setChangeError] = useState('');
  const [changeSuccess, setChangeSuccess] = useState(false);

  // Client folders state
  const [clientFolders, setClientFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [folderFiles, setFolderFiles] = useState([]);
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [folderForm, setFolderForm] = useState({ client_name: '', client_email: '', client_phone: '', notes: '' });
  const [folderLoading, setFolderLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [folderSearch, setFolderSearch] = useState('');
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [selectedFileIds, setSelectedFileIds] = useState([]);  // ordered
  const [mergeRefreshKey, setMergeRefreshKey] = useState(0);
  const clientFileInputRef = useRef(null);

  const toggleFileSelect = (fileId) => {
    setSelectedFileIds(prev =>
      prev.includes(fileId) ? prev.filter(id => id !== fileId) : [...prev, fileId]
    );
  };
  const clearFileSelection = () => {
    setSelectedFileIds([]);
    setMergeRefreshKey(k => k + 1);
  };

  // Common files state (view only for agents)
  const [commonFiles, setCommonFiles] = useState([]);
  const [commonFilesCategory, setCommonFilesCategory] = useState('all');

  // Site settings state
  const [siteSettings, setSiteSettings] = useState({ intranet_url: '' });

  // Tasks state
  const [tasks, setTasks] = useState([]);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskForm, setTaskForm] = useState({ title: '', description: '', due_date: '', due_time: '', priority: 'medium', category: 'general' });
  const [taskFilter, setTaskFilter] = useState('all'); // all, pending, completed

  // Calendar state
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [showEventModal, setShowEventModal] = useState(false);
  const [companyEventView, setCompanyEventView] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({ title: '', description: '', start_date: '', start_time: '09:00', end_time: '10:00', all_day: false, color: '#0077B6' });
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Fetch tasks
  const fetchTasks = async () => {
    if (!agent) return;
    try {
      const response = await fetch(`${API_URL}/api/agents/${agent.id}/tasks`);
      if (response.ok) {
        const data = await response.json();
        setTasks(data);
      }
    } catch (err) {
      console.error('Failed to fetch tasks:', err);
    }
  };

  // Fetch calendar events
  const fetchCalendarEvents = async () => {
    if (!agent) return;
    try {
      const month = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`;
      const response = await fetch(`${API_URL}/api/agents/${agent.id}/calendar?month=${month}`);
      if (response.ok) {
        const data = await response.json();
        setCalendarEvents(data);
      }
    } catch (err) {
      console.error('Failed to fetch calendar events:', err);
    }
  };

  // Fetch client folders
  const fetchClientFolders = async () => {
    if (!agent) return;
    try {
      const response = await fetch(`${API_URL}/api/client-folders/${agent.id}`);
      if (response.ok) {
        const data = await response.json();
        setClientFolders(data);
      }
    } catch (err) {
      console.error('Failed to fetch folders:', err);
    }
  };

  // Fetch folder files
  const fetchFolderFiles = async (folderId) => {
    try {
      const response = await fetch(`${API_URL}/api/client-files/${folderId}`);
      if (response.ok) {
        const data = await response.json();
        setFolderFiles(data);
      }
    } catch (err) {
      console.error('Failed to fetch files:', err);
    }
  };

  // Fetch common files (filtered by agent's licenses)
  const fetchCommonFiles = async () => {
    if (!agent) return;
    try {
      const licenses = agent.licenses?.join(',') || '';
      const url = commonFilesCategory === 'all' 
        ? `${API_URL}/api/common-files?licenses=${licenses}`
        : `${API_URL}/api/common-files?category=${commonFilesCategory}&licenses=${licenses}`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setCommonFiles(data);
      }
    } catch (err) {
      console.error('Failed to fetch common files:', err);
    }
  };

  // Fetch site settings
  const fetchSiteSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings`);
      if (response.ok) {
        const data = await response.json();
        setSiteSettings(data);
      }
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    }
  };

  useEffect(() => {
    if (agent && activeTab === 'clients') {
      fetchClientFolders();
    }
    if (agent && activeTab === 'common') {
      fetchCommonFiles();
    }
    if (agent && activeTab === 'tasks') {
      fetchTasks();
    }
    if (agent && activeTab === 'calendar') {
      fetchCalendarEvents();
    }
    if (agent) {
      fetchSiteSettings();
    }
  }, [agent, activeTab, commonFilesCategory]);

  useEffect(() => {
    if (agent && activeTab === 'calendar') {
      fetchCalendarEvents();
    }
  }, [currentMonth]);

  useEffect(() => {
    if (selectedFolder) {
      fetchFolderFiles(selectedFolder.id);
      setSelectedFileIds([]);
    }
  }, [selectedFolder]);

  // Load this agent's saved tab ordering (persisted per-account, cross-device).
  const tabPrefKey = agent ? `agent_tab_order:${agent.id}` : null;
  useEffect(() => {
    if (!agent) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/preferences/${tabPrefKey}`);
        if (res.ok) {
          const data = await res.json();
          setTabOrder(Array.isArray(data.tab_order) ? data.tab_order : []);
        }
      } catch (err) {
        console.error('Failed to load tab order:', err);
      }
    })();
  }, [agent?.id]);

  const saveTabOrder = async (orderedIds) => {
    setTabOrder(orderedIds);
    try {
      await fetch(`${API_URL}/api/preferences/${tabPrefKey}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tab_order: orderedIds }),
      });
    } catch (err) {
      console.error('Failed to save tab order:', err);
    }
  };

  const resetTabOrder = () => saveTabOrder([]);

  // Apply a saved id ordering to the available tabs; new/unknown tabs fall to the end.
  const applyTabOrder = (tabs, order) => {
    if (!order || !order.length) return tabs;
    const byId = Object.fromEntries(tabs.map((t) => [t.id, t]));
    const ordered = order.map((id) => byId[id]).filter(Boolean);
    const remaining = tabs.filter((t) => !order.includes(t.id));
    return [...ordered, ...remaining];
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/agents/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (response.ok) {
        setSession('agent', data.token);
        if (data.download_token) setDownloadToken(data.download_token, 1800);
        setAgent(data.agent);
        sessionStorage.setItem('agent_session', JSON.stringify(data.agent));
      } else {
        setError(typeof data.detail === 'string' ? data.detail : 'Login failed. Please check your credentials.');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotResult(null);

    try {
      const response = await fetch(`${API_URL}/api/agents/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail })
      });

      const data = await response.json();
      setForgotResult(data);
    } catch (err) {
      setForgotResult({ message: 'Connection error. Please try again.', error: true });
    } finally {
      setForgotLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setChangeError('');
    setChangeSuccess(false);

    if (newPassword !== confirmPassword) {
      setChangeError('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setChangeError('New password must be at least 8 characters');
      return;
    }

    setChangeLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/agents/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          current_password: currentPassword, 
          new_password: newPassword 
        })
      });

      const data = await response.json();

      if (response.ok) {
        setChangeSuccess(true);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setTimeout(() => {
          setShowChangePassword(false);
          setChangeSuccess(false);
        }, 2000);
      } else {
        setChangeError(data.detail || 'Failed to change password');
      }
    } catch (err) {
      setChangeError('Connection error. Please try again.');
    } finally {
      setChangeLoading(false);
    }
  };

  const handleLogout = () => {
    setAgent(null);
    clearSession('agent');
    sessionStorage.removeItem('agent_session');
    setEmail('');
    setPassword('');
    setActiveTab('dashboard');
    setSelectedFolder(null);
  };

  // Client Folder handlers
  const handleCreateFolder = async (e) => {
    e.preventDefault();
    setFolderLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/client-folders/${agent.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(folderForm)
      });
      if (response.ok) {
        fetchClientFolders();
        setShowNewFolderModal(false);
        setFolderForm({ client_name: '', client_email: '', client_phone: '', notes: '' });
      }
    } catch (err) {
      console.error('Failed to create folder:', err);
    } finally {
      setFolderLoading(false);
    }
  };

  const handleUpdateFolder = async (e) => {
    e.preventDefault();
    setFolderLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/client-folders/${editingFolder.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(folderForm)
      });
      if (response.ok) {
        fetchClientFolders();
        setEditingFolder(null);
        setFolderForm({ client_name: '', client_email: '', client_phone: '', notes: '' });
      }
    } catch (err) {
      console.error('Failed to update folder:', err);
    } finally {
      setFolderLoading(false);
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm('Are you sure you want to delete this folder and all its files?')) return;
    try {
      const response = await fetch(`${API_URL}/api/client-folders/${folderId}`, { method: 'DELETE' });
      if (response.ok) {
        fetchClientFolders();
        if (selectedFolder?.id === folderId) {
          setSelectedFolder(null);
          setFolderFiles([]);
        }
      }
    } catch (err) {
      console.error('Failed to delete folder:', err);
    }
  };

  const handleUploadClientFiles = async (eOrFileList) => {
    // Accept either input change event or a FileList (drag-drop).
    const files = eOrFileList?.target?.files ?? eOrFileList;
    if (!files || files.length === 0 || !selectedFolder) return;

    // One request per file: no proxy timeout / body-size limit on big batches, and a
    // single bad file never cancels the rest.
    setUploadingFile(true);
    const list = Array.from(files);
    const failed = [];
    for (let i = 0; i < list.length; i++) {
      setUploadProgress({ done: i, total: list.length, name: list[i].name });
      const formData = new FormData();
      formData.append('files', list[i]);
      try {
        const response = await fetch(
          `${API_URL}/api/client-files/${selectedFolder.id}?agent_id=${agent.id}`,
          { method: 'POST', body: formData }
        );
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          failed.push(`${list[i].name}: ${typeof err.detail === 'string' ? err.detail : 'upload failed'}`);
        }
      } catch (err) {
        failed.push(`${list[i].name}: network error`);
      }
      if ((i + 1) % 3 === 0) fetchFolderFiles(selectedFolder.id);
    }
    await fetchFolderFiles(selectedFolder.id);
    setUploadingFile(false);
    setUploadProgress(null);
    if (clientFileInputRef.current) clientFileInputRef.current.value = '';
    if (failed.length) alert(`${list.length - failed.length} of ${list.length} files uploaded.\n\nFailed:\n${failed.join('\n')}`);
  };

  const handleDeleteClientFile = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;
    try {
      const response = await fetch(`${API_URL}/api/client-files/file/${fileId}`, { method: 'DELETE' });
      if (response.ok) {
        fetchFolderFiles(selectedFolder.id);
      }
    } catch (err) {
      console.error('Failed to delete file:', err);
    }
  };

  const handleRenameClientFile = async (fileId, currentName) => {
    const next = window.prompt('Rename file:', currentName || '');
    if (next === null) return;
    const trimmed = next.trim();
    if (!trimmed || trimmed === currentName) return;
    try {
      const r = await fetch(`${API_URL}/api/client-files/file/${fileId}/rename`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: trimmed }),
      });
      if (r.ok) fetchFolderFiles(selectedFolder.id);
      else alert((await r.json())?.detail || 'Rename failed.');
    } catch (err) {
      console.error('Rename failed:', err);
    }
  };

  // ===== TASK HANDLERS =====
  const handleSaveTask = async (e) => {
    e.preventDefault();
    try {
      const url = editingTask 
        ? `${API_URL}/api/agents/${agent.id}/tasks/${editingTask.id}`
        : `${API_URL}/api/agents/${agent.id}/tasks`;
      
      const response = await fetch(url, {
        method: editingTask ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(taskForm)
      });
      
      if (response.ok) {
        fetchTasks();
        setShowTaskModal(false);
        setEditingTask(null);
        setTaskForm({ title: '', description: '', due_date: '', due_time: '', priority: 'medium', category: 'general' });
      }
    } catch (err) {
      console.error('Failed to save task:', err);
    }
  };

  const handleToggleTaskStatus = async (task) => {
    const newStatus = task.status === 'completed' ? 'pending' : 'completed';
    try {
      await fetch(`${API_URL}/api/agents/${agent.id}/tasks/${task.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      fetchTasks();
    } catch (err) {
      console.error('Failed to update task:', err);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await fetch(`${API_URL}/api/agents/${agent.id}/tasks/${taskId}`, { method: 'DELETE' });
      fetchTasks();
    } catch (err) {
      console.error('Failed to delete task:', err);
    }
  };

  // ===== CALENDAR HANDLERS =====
  const handleSaveEvent = async (e) => {
    e.preventDefault();
    try {
      const url = editingEvent 
        ? `${API_URL}/api/agents/${agent.id}/calendar/${editingEvent.id}`
        : `${API_URL}/api/agents/${agent.id}/calendar`;
      
      const response = await fetch(url, {
        method: editingEvent ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(eventForm)
      });
      
      if (response.ok) {
        fetchCalendarEvents();
        setShowEventModal(false);
        setEditingEvent(null);
        setEventForm({ title: '', description: '', start_date: '', start_time: '09:00', end_time: '10:00', all_day: false, color: '#0077B6' });
      }
    } catch (err) {
      console.error('Failed to save event:', err);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await fetch(`${API_URL}/api/agents/${agent.id}/calendar/${eventId}`, { method: 'DELETE' });
      fetchCalendarEvents();
    } catch (err) {
      console.error('Failed to delete event:', err);
    }
  };

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    return { daysInMonth, startingDay };
  };

  const getEventsForDate = (date) => {
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(date).padStart(2, '0')}`;
    return calendarEvents.filter(event => event.start_date === dateStr);
  };

  const filteredTasks = tasks.filter(task => {
    if (taskFilter === 'all') return true;
    if (taskFilter === 'pending') return task.status !== 'completed';
    if (taskFilter === 'completed') return task.status === 'completed';
    return true;
  });

  // Helper function for category labels
  const getCategoryLabel = (category) => {
    const labels = {
      'company': 'Company',
      'mortgage': 'Mortgage',
      'insurance_investments': 'Insurance & Investments'
    };
    return labels[category] || category;
  };

  // Get allowed categories based on agent's licenses
  const getAllowedCategories = () => {
    if (!agent) return ['company'];
    const licenses = agent.licenses || [];
    const categories = ['all', 'company'];
    if (licenses.includes('mortgage')) categories.push('mortgage');
    if (licenses.includes('insurance') || licenses.includes('investments')) categories.push('insurance_investments');
    return categories;
  };

  // Check for existing session on mount (token must still exist, otherwise treat as logged out)
  useEffect(() => {
    const session = sessionStorage.getItem('agent_session');
    if (session && getToken('agent')) {
      setAgent(JSON.parse(session));
    } else if (session) {
      sessionStorage.removeItem('agent_session');
    }
    const onExpired = (e) => {
      if (e.detail?.role === 'agent') {
        clearSession('agent');
        sessionStorage.removeItem('agent_session');
        setAgent(null);
        setError('Your session has expired. Please sign in again.');
      }
    };
    window.addEventListener('utmost-auth-expired', onExpired);
    return () => window.removeEventListener('utmost-auth-expired', onExpired);
  }, []);

  // Filter folders by search
  const filteredFolders = clientFolders.filter(folder => 
    folder.client_name.toLowerCase().includes(folderSearch.toLowerCase()) ||
    folder.client_email?.toLowerCase().includes(folderSearch.toLowerCase())
  );

  // Password reset via emailed link (?reset=TOKEN)
  const resetToken = new URLSearchParams(window.location.search).get('reset');
  if (resetToken && !agent) {
    return (
      <ResetPasswordForm
        token={resetToken}
        endpoint="/api/agents/reset-password"
        onDone={() => { window.history.replaceState({}, '', '/agent-login'); window.location.reload(); }}
      />
    );
  }

  // Forgot Password View
  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#003D5C] to-[#0077B6] flex items-center justify-center py-12 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4">
              <KeyRound className="h-8 w-8 text-[#003D5C]" />
            </div>
            <CardTitle className="text-2xl">Reset Password</CardTitle>
            <p className="text-gray-600 mt-2">Enter your email and we'll send you a secure reset link</p>
          </CardHeader>
          <CardContent>
            {forgotResult ? (
              <div className="space-y-4">
                <div className={`p-4 rounded-lg ${forgotResult.error ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`} data-testid="forgot-result">
                  <p className={`text-sm ${forgotResult.error ? 'text-red-600' : 'text-green-700'}`}>
                    {forgotResult.message}
                  </p>
                  {!forgotResult.error && (
                    <p className="text-xs text-gray-500 mt-2">The link expires in 1 hour. Check your spam folder if you don't see it.</p>
                  )}
                </div>
                <Button
                  onClick={() => { setShowForgotPassword(false); setForgotResult(null); setForgotEmail(''); }}
                  className="w-full bg-[#0077B6] hover:bg-[#005A8C]"
                >
                  Back to Login
                </Button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                      placeholder="your.email@example.com"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent"
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>
                <Button type="submit" disabled={forgotLoading} className="w-full bg-[#0077B6] hover:bg-[#005A8C]" data-testid="reset-password-btn">
                  {forgotLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending...</> : 'Send Reset Link'}
                </Button>
                <button type="button" onClick={() => setShowForgotPassword(false)} className="w-full text-sm text-gray-600 hover:text-[#0077B6] flex items-center justify-center gap-2">
                  <ArrowLeft className="h-4 w-4" />Back to Login
                </button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Agent Dashboard View
  if (agent) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#003D5C] via-[#0077B6] to-[#00A8E8] text-white py-6">
          <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
            <div className="flex items-center gap-4">
              <a 
                href="/" 
                className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
                title="Go to Main Website"
              >
                <Home className="h-5 w-5" />
                <span className="hidden sm:inline text-sm">Main Website</span>
              </a>
              <div className="h-8 w-px bg-white/30 hidden sm:block"></div>
              <div>
                <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white via-[#D4AF37] to-white">Agent Portal</h1>
                <p className="text-blue-100 text-sm mt-1">Welcome, <span className="font-semibold text-[#D4AF37]">{agent.name}</span></p>
              </div>
            </div>
            <Button variant="outline" onClick={handleLogout} className="border-white text-white hover:bg-white hover:text-[#003D5C]">
              Logout
            </Button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white border-b shadow-sm">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-end pt-2">
              <DragHint />
              {tabOrder.length > 0 && (
                <button
                  onClick={resetTabOrder}
                  data-testid="reset-tab-order-btn"
                  className="ml-3 text-xs text-gray-400 hover:text-amber-600 underline underline-offset-2"
                >
                  Reset order
                </button>
              )}
            </div>
            {(() => {
              // Iter 39: gate the Compliance Files tab to insurance/investment
              // licensees and the My Clients' Folders tab to mortgage agents.
              // Admin (agent.id === 'admin') sees everything.
              const licenses = (agent?.licenses || []).map(l => (l || '').toLowerCase());
              const isAdmin = agent?.id === 'admin';
              const hasInsuranceOrInvestments = isAdmin || licenses.some(l => l === 'insurance' || l === 'investments' || l === 'investment');
              const hasMortgage = isAdmin || licenses.includes('mortgage');
              const allTabs = [
                { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, color: 'from-[#0A2A43] to-[#114a73]', hoverBg: 'hover:bg-slate-50' },
                { id: 'profile', label: 'My Profile', icon: User, color: 'from-blue-500 to-blue-600', hoverBg: 'hover:bg-blue-50' },
                { id: 'carriers', label: 'My Carriers', icon: Briefcase, color: 'from-amber-500 to-yellow-500', hoverBg: 'hover:bg-amber-50' },
                hasInsuranceOrInvestments && { id: 'compliance', label: 'Insurance App Workflow', icon: FileSpreadsheet, color: 'from-teal-500 to-cyan-500', hoverBg: 'hover:bg-teal-50' },
                hasMortgage && { id: 'mortgage-workflow', label: 'Mortgage App Workflow', icon: Banknote, color: 'from-blue-600 to-sky-600', hoverBg: 'hover:bg-blue-50' },
                { id: 'tasks', label: 'Tasks', icon: ListTodo, color: 'from-rose-500 to-pink-500', hoverBg: 'hover:bg-rose-50' },
                { id: 'calendar', label: 'Calendar', icon: CalendarDays, color: 'from-indigo-500 to-violet-500', hoverBg: 'hover:bg-indigo-50' },
                hasMortgage && { id: 'clients', label: "My Clients' Folders", icon: Folder, color: 'from-emerald-500 to-emerald-600', hoverBg: 'hover:bg-emerald-50' },
                { id: 'common', label: 'Common Files', icon: Building, color: 'from-purple-500 to-purple-600', hoverBg: 'hover:bg-purple-50' },
                { id: 'regulatory', label: 'Regulatory Forms', icon: FileText, color: 'from-red-500 to-red-600', hoverBg: 'hover:bg-red-50' },
                { id: 'my-documents', label: 'My Documents', icon: Shield, color: 'from-sky-500 to-blue-600', hoverBg: 'hover:bg-sky-50' },
                { id: 'translation', label: 'Translation', icon: Languages, color: 'from-fuchsia-500 to-pink-500', hoverBg: 'hover:bg-fuchsia-50' },
                { id: 'memos', label: 'Memos & Notices', icon: Mail, color: 'from-blue-600 to-sky-500', hoverBg: 'hover:bg-blue-50' },
                siteSettings.intranet_url && { id: 'intranet', label: 'UTFI Intranet', icon: ExternalLink, external: true, color: 'from-amber-500 to-orange-500', hoverBg: 'hover:bg-amber-50' },
              ].filter(Boolean);
              const orderedTabs = applyTabOrder(allTabs, tabOrder);
              return (
                <DraggableTabs
                  tabs={orderedTabs}
                  onReorder={saveTabOrder}
                  className="flex gap-2 overflow-x-auto"
                  renderTab={(tab) => (
                    <button
                      onClick={() => {
                        if (tab.external && tab.id === 'intranet') {
                          window.open(siteSettings.intranet_url, '_blank');
                        } else {
                          setActiveTab(tab.id);
                          setSelectedFolder(null);
                        }
                      }}
                      className={`flex items-center gap-2 px-5 py-3 rounded-t-lg font-medium transition-all duration-200 whitespace-nowrap ${
                        activeTab === tab.id
                          ? `bg-gradient-to-r ${tab.color} text-white shadow-md`
                          : `text-gray-600 ${tab.hoverBg} hover:text-gray-800`
                      }`}
                    >
                      <tab.icon className={`h-4 w-4 ${activeTab === tab.id ? 'text-white' : ''}`} />
                      {tab.label}
                      {tab.external && <ExternalLink className="h-3 w-3 ml-1" />}
                    </button>
                  )}
                />
              );
            })()}
          </div>
        </div>


        <div className="max-w-7xl mx-auto px-4 py-6">
          <AutopilotBanner apiUrl={API_URL} agentId={agent.id} />
          {activeTab === 'dashboard' && <AgentDashboard apiUrl={API_URL} agent={agent} onNavigate={setActiveTab} />}
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="space-y-6">
              {/* Profile Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5 text-[#0077B6]" />Your Profile
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="flex-shrink-0">
                      <TeamPhoto src={agent.photo} name={agent.name} className="w-32 h-32 rounded-full object-cover border-4 border-[#D4AF37]" />
                    </div>
                    <div className="flex-1 space-y-3">
                      <div><label className="text-sm text-gray-500">Name</label><p className="text-lg font-semibold text-gray-800">{agent.name}</p></div>
                      <div><label className="text-sm text-gray-500">Role</label><p className="text-gray-800">{agent.role}</p></div>
                      <div className="grid grid-cols-2 gap-4">
                        <div><label className="text-sm text-gray-500">Email</label><p className="text-gray-800 flex items-center gap-2"><Mail className="h-4 w-4 text-[#0077B6]" />{agent.email}</p></div>
                        <div><label className="text-sm text-gray-500">Phone</label><p className="text-gray-800 flex items-center gap-2"><Phone className="h-4 w-4 text-[#0077B6]" />{agent.phone}</p></div>
                      </div>
                      <div><label className="text-sm text-gray-500">Licensed In</label><p className="text-gray-800">{agent.provinces}</p></div>
                      <BirthdayField agent={agent} onUpdate={(merged) => setAgent(merged)} />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Licences & Compliance Details (Iter 65) */}
              <AdvisorComplianceProfile agent={agent} onUpdate={(merged) => setAgent(merged)} />

              {/* Website Card */}
              <Card>
                <CardHeader><CardTitle className="flex items-center gap-2"><Globe className="h-5 w-5 text-[#0077B6]" />Your Personal Website</CardTitle></CardHeader>
                <CardContent>
                  <div className="bg-[#F0F7FF] p-4 rounded-lg">
                    <p className="text-sm text-gray-600 mb-2">Share this link with your clients:</p>
                    <div className="flex items-center gap-3">
                      <code className="flex-1 bg-white px-4 py-2 rounded border text-[#0077B6] font-mono text-sm">{window.location.origin}/agent/{agent.slug}</code>
                      <Button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/agent/${agent.slug}`); alert('URL copied!'); }} variant="outline">Copy</Button>
                      <Button onClick={() => window.open(`/agent/${agent.slug}`, '_blank')} className="bg-[#0077B6]">View</Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Commission Portal Card */}
              {agent.commission_portal_url && (
                <Card>
                  <CardHeader><CardTitle className="flex items-center gap-2"><Briefcase className="h-5 w-5 text-[#D4AF37]" />Your Commission Portal</CardTitle></CardHeader>
                  <CardContent>
                    <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-4 rounded-lg border border-amber-200">
                      <p className="text-sm text-gray-600 mb-2">Access your commission statements and reports:</p>
                      <div className="flex items-center gap-3">
                        <code className="flex-1 bg-white px-4 py-2 rounded border text-amber-700 font-mono text-sm truncate">{agent.commission_portal_url}</code>
                        <Button onClick={() => window.open(agent.commission_portal_url, '_blank')} className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white">
                          <ExternalLink className="h-4 w-4 mr-2" />Open Portal
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Security Card */}
              <Card>
                <CardHeader><CardTitle className="flex items-center gap-2"><Lock className="h-5 w-5 text-[#0077B6]" />Security Settings</CardTitle></CardHeader>
                <CardContent>
                  {showChangePassword ? (
                    <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
                      {changeSuccess && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2"><CheckCircle className="h-4 w-4" />Password changed successfully!</div>}
                      {changeError && <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">{changeError}</div>}
                      <div><label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label><input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6]" /></div>
                      <div><label className="block text-sm font-medium text-gray-700 mb-1">New Password</label><input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={8} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6]" /><p className="text-xs text-gray-500 mt-1">Must be at least 8 characters</p></div>
                      <div><label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label><input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6]" /></div>
                      <div className="flex gap-3">
                        <Button type="button" variant="outline" onClick={() => { setShowChangePassword(false); setChangeError(''); setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); }}>Cancel</Button>
                        <Button type="submit" disabled={changeLoading} className="bg-[#0077B6]">{changeLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : 'Save New Password'}</Button>
                      </div>
                    </form>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div><p className="font-medium text-gray-800">Password</p><p className="text-sm text-gray-500">Change your account password</p></div>
                      <Button onClick={() => setShowChangePassword(true)} variant="outline"><KeyRound className="h-4 w-4 mr-2" />Change Password</Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Compliance Tab */}
          {activeTab === 'compliance' && (
            <ComplianceApp agent={agent} isAdmin={false} />
          )}

          {activeTab === 'mortgage-workflow' && (
            <MortgageApp agent={agent} isAdmin={agent?.id === 'admin'} />
          )}

          {/* Carrier Affiliations Tab */}
          {activeTab === 'carriers' && (
            <div className="space-y-6">
              <CarrierAffiliations agentId={agent.id} />
            </div>
          )}

          {/* Tasks Tab */}
          {activeTab === 'tasks' && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <CardTitle className="flex items-center gap-2">
                      <ListTodo className="h-5 w-5 text-rose-500" />
                      My Tasks
                    </CardTitle>
                    <div className="flex items-center gap-3">
                      <div className="flex rounded-lg bg-gray-100 p-1">
                        {['all', 'pending', 'completed'].map(filter => (
                          <button
                            key={filter}
                            onClick={() => setTaskFilter(filter)}
                            className={`px-3 py-1 text-sm rounded-md transition-colors ${
                              taskFilter === filter ? 'bg-white shadow text-rose-500 font-medium' : 'text-gray-600 hover:text-gray-800'
                            }`}
                          >
                            {filter.charAt(0).toUpperCase() + filter.slice(1)}
                          </button>
                        ))}
                      </div>
                      <Button onClick={() => { setShowTaskModal(true); setEditingTask(null); setTaskForm({ title: '', description: '', due_date: '', due_time: '', priority: 'medium', category: 'general' }); }} className="bg-rose-500 hover:bg-rose-600">
                        <Plus className="h-4 w-4 mr-2" />Add Task
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {filteredTasks.length === 0 ? (
                    <div className="py-12 text-center text-gray-500">
                      <ListTodo className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                      <p>No tasks {taskFilter !== 'all' ? `(${taskFilter})` : ''}</p>
                      <p className="text-sm">Add a task to stay organized</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredTasks.map(task => (
                        <div 
                          key={task.id} 
                          className={`flex items-start gap-3 p-4 rounded-lg border ${
                            task.status === 'completed' ? 'bg-gray-50 border-gray-200' : 
                            task.priority === 'high' ? 'bg-red-50 border-red-200' :
                            task.priority === 'medium' ? 'bg-amber-50 border-amber-200' :
                            'bg-white border-gray-200'
                          }`}
                        >
                          <button 
                            onClick={() => handleToggleTaskStatus(task)}
                            className="mt-1 flex-shrink-0"
                          >
                            {task.status === 'completed' ? (
                              <CheckSquare className="h-5 w-5 text-green-500" />
                            ) : (
                              <Square className="h-5 w-5 text-gray-400 hover:text-rose-500" />
                            )}
                          </button>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className={`font-medium ${task.status === 'completed' ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                                {task.title}
                              </h4>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                task.priority === 'high' ? 'bg-red-100 text-red-700' :
                                task.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                                {task.priority}
                              </span>
                              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                                {task.category}
                              </span>
                            </div>
                            {task.description && (
                              <p className={`text-sm mt-1 ${task.status === 'completed' ? 'text-gray-400' : 'text-gray-600'}`}>
                                <LinkifiedText text={task.description} />
                              </p>
                            )}
                            {task.due_date && (
                              <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
                                <Clock className="h-3 w-3" />
                                <span>Due: {task.due_date} {task.due_time && `at ${task.due_time}`}</span>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => { setEditingTask(task); setTaskForm({ title: task.title, description: task.description || '', due_date: task.due_date || '', due_time: task.due_time || '', priority: task.priority, category: task.category }); setShowTaskModal(true); }}
                              className="p-1 hover:bg-gray-200 rounded"
                            >
                              <Edit2 className="h-4 w-4 text-gray-500" />
                            </button>
                            <button
                              onClick={() => handleDeleteTask(task.id)}
                              className="p-1 hover:bg-red-100 rounded"
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Calendar Tab */}
          {activeTab === 'calendar' && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <CardTitle className="flex items-center gap-2">
                      <CalendarDays className="h-5 w-5 text-indigo-500" />
                      My Calendar
                    </CardTitle>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                          className="p-1 hover:bg-gray-100 rounded"
                        >
                          <ChevronLeft className="h-5 w-5" />
                        </button>
                        <span className="font-medium text-lg min-w-[150px] text-center">
                          {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </span>
                        <button 
                          onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                          className="p-1 hover:bg-gray-100 rounded"
                        >
                          <ChevronRight className="h-5 w-5" />
                        </button>
                      </div>
                      <Button onClick={() => { setShowEventModal(true); setEditingEvent(null); setEventForm({ title: '', description: '', start_date: '', start_time: '09:00', end_time: '10:00', all_day: false, color: '#0077B6' }); }} className="bg-indigo-500 hover:bg-indigo-600">
                        <Plus className="h-4 w-4 mr-2" />Add Event
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Calendar Grid */}
                  <div className="grid grid-cols-7 gap-1">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                      <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                        {day}
                      </div>
                    ))}
                    {(() => {
                      const { daysInMonth, startingDay } = getDaysInMonth(currentMonth);
                      const days = [];
                      
                      // Empty cells for days before the first day of the month
                      for (let i = 0; i < startingDay; i++) {
                        days.push(<div key={`empty-${i}`} className="h-24 bg-gray-50 rounded"></div>);
                      }
                      
                      // Days of the month
                      for (let day = 1; day <= daysInMonth; day++) {
                        const dayEvents = getEventsForDate(day);
                        const isToday = new Date().toDateString() === new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day).toDateString();
                        
                        days.push(
                          <div 
                            key={day} 
                            className={`h-24 border rounded p-1 overflow-hidden ${isToday ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200'} hover:border-indigo-300 cursor-pointer`}
                            onClick={() => {
                              const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                              setEventForm({ ...eventForm, start_date: dateStr });
                              setShowEventModal(true);
                              setEditingEvent(null);
                            }}
                          >
                            <div className={`text-sm font-medium ${isToday ? 'text-blue-600' : 'text-gray-700'}`}>
                              {day}
                            </div>
                            <div className="space-y-0.5 mt-1">
                              {dayEvents.slice(0, 2).map(event => {
                                const isAdmin = event.source === 'admin' || event.read_only;
                                return (
                                <div 
                                  key={event.id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (isAdmin) {
                                      setCompanyEventView(event);
                                      return;
                                    }
                                    setEditingEvent(event);
                                    setEventForm({ title: event.title, description: event.description || '', start_date: event.start_date, start_time: event.start_time || '09:00', end_time: event.end_time || '10:00', all_day: event.all_day || false, color: event.color || '#0077B6' });
                                    setShowEventModal(true);
                                  }}
                                  className={`text-xs px-1 py-0.5 rounded truncate text-white flex items-center gap-1 ${isAdmin ? 'ring-1 ring-white/40 cursor-help' : 'cursor-pointer'}`}
                                  style={{ backgroundColor: event.color || '#0077B6' }}
                                  title={isAdmin ? `Company event (read-only) — ${event.title}` : event.title}
                                >
                                  {isAdmin && <Lock className="h-2.5 w-2.5 flex-shrink-0" />}
                                  <span className="truncate">{event.title}</span>
                                </div>
                                );
                              })}
                              {dayEvents.length > 2 && (
                                <div className="text-xs text-gray-500 px-1">+{dayEvents.length - 2} more</div>
                              )}
                            </div>
                          </div>
                        );
                      }
                      
                      return days;
                    })()}
                  </div>
                  
                    {/* Upcoming Events List */}
                  <div className="mt-6 pt-6 border-t">
                    <h3 className="font-medium text-gray-800 mb-3">Upcoming Events</h3>
                    {calendarEvents.length === 0 ? (
                      <p className="text-sm text-gray-500">No events this month</p>
                    ) : (
                      <div className="space-y-2">
                        {calendarEvents.slice(0, 5).map(event => {
                          const isAdmin = event.source === 'admin' || event.read_only;
                          return (
                          <div key={event.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: event.color || '#0077B6' }}></div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm flex items-center gap-1.5">
                                {event.title}
                                {isAdmin && (
                                  <span className="inline-flex items-center gap-0.5 bg-indigo-100 text-indigo-700 text-[10px] font-semibold px-1.5 py-0.5 rounded-full">
                                    <Lock className="h-2.5 w-2.5" /> Company
                                  </span>
                                )}
                              </p>
                              <p className="text-xs text-gray-500">{event.start_date} {!event.all_day && `at ${event.start_time}`}</p>
                              {event.description && (
                                <p className="text-xs text-gray-600 mt-0.5">
                                  <LinkifiedText text={event.description} />
                                </p>
                              )}
                            </div>
                            <div className="flex gap-1">
                              {!isAdmin && (
                                <>
                                  <button onClick={() => { setEditingEvent(event); setEventForm({ title: event.title, description: event.description || '', start_date: event.start_date, start_time: event.start_time || '09:00', end_time: event.end_time || '10:00', all_day: event.all_day || false, color: event.color || '#0077B6' }); setShowEventModal(true); }} className="p-1 hover:bg-gray-200 rounded">
                                    <Edit2 className="h-3 w-3 text-gray-500" />
                                  </button>
                                  <button onClick={() => handleDeleteEvent(event.id)} className="p-1 hover:bg-red-100 rounded">
                                    <Trash2 className="h-3 w-3 text-red-500" />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Client Folders Tab */}
          {activeTab === 'clients' && (
            <div className="grid md:grid-cols-3 gap-6">
              {/* Folders List */}
              <div className="md:col-span-1">
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">Client Folders</CardTitle>
                      <Button size="sm" onClick={() => { setShowNewFolderModal(true); setFolderForm({ client_name: '', client_email: '', client_phone: '', notes: '' }); }} className="bg-[#0077B6]">
                        <FolderPlus className="h-4 w-4 mr-1" />New
                      </Button>
                    </div>
                    <div className="relative mt-2">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search clients..."
                        value={folderSearch}
                        onChange={(e) => setFolderSearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6]"
                      />
                    </div>
                  </CardHeader>
                  <CardContent className="p-0 max-h-[500px] overflow-y-auto">
                    {filteredFolders.length === 0 ? (
                      <div className="p-6 text-center text-gray-500">
                        <Folder className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                        <p>No client folders yet</p>
                        <p className="text-sm">Create your first folder to get started</p>
                      </div>
                    ) : (
                      <div className="divide-y">
                        {filteredFolders.map(folder => (
                          <div
                            key={folder.id}
                            onClick={() => setSelectedFolder(folder)}
                            className={`p-3 cursor-pointer hover:bg-gray-50 flex items-center justify-between group ${
                              selectedFolder?.id === folder.id ? 'bg-[#F0F7FF] border-l-4 border-[#0077B6]' : ''
                            }`}
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <Folder className={`h-5 w-5 flex-shrink-0 ${selectedFolder?.id === folder.id ? 'text-[#0077B6]' : 'text-[#D4AF37]'}`} />
                              <div className="min-w-0">
                                <p className="font-medium text-gray-800 truncate">{folder.client_name}</p>
                                {folder.client_email && <p className="text-xs text-gray-500 truncate">{folder.client_email}</p>}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={(e) => { e.stopPropagation(); setEditingFolder(folder); setFolderForm({ client_name: folder.client_name, client_email: folder.client_email || '', client_phone: folder.client_phone || '', notes: folder.notes || '' }); }}
                                className="p-1 hover:bg-gray-200 rounded"
                              >
                                <Edit2 className="h-4 w-4 text-gray-500" />
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder.id); }}
                                className="p-1 hover:bg-red-100 rounded"
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Folder Contents */}
              <div className="md:col-span-2">
                <Card>
                  <CardHeader className="pb-3">
                    {selectedFolder ? (
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Folder className="h-5 w-5 text-[#D4AF37]" />
                            {selectedFolder.client_name}
                          </CardTitle>
                          {selectedFolder.client_email && <p className="text-sm text-gray-500">{selectedFolder.client_email}</p>}
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <RequestDocsButton folder={selectedFolder} apiUrl={API_URL} />
                          <input ref={clientFileInputRef} type="file" multiple onChange={handleUploadClientFiles} className="hidden" />
                          <Button onClick={() => clientFileInputRef.current?.click()} disabled={uploadingFile} className="bg-[#0077B6]" data-testid="agent-upload-files-btn">
                            {uploadingFile ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                            {uploadProgress ? <span data-testid="upload-progress">Uploading {uploadProgress.done + 1}/{uploadProgress.total}…</span> : 'Upload Files'}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <CardTitle className="text-lg text-gray-400">Select a folder to view files</CardTitle>
                    )}
                  </CardHeader>
                  <CardContent>
                    {selectedFolder ? (
                      <div
                        onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); if (e.dataTransfer?.types?.includes('Files')) setIsDraggingOver(true); }}
                        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (e.dataTransfer?.types?.includes('Files')) setIsDraggingOver(true); }}
                        onDragLeave={(e) => {
                          e.preventDefault(); e.stopPropagation();
                          // Only clear if leaving the drop wrapper itself.
                          if (e.currentTarget.contains(e.relatedTarget)) return;
                          setIsDraggingOver(false);
                        }}
                        onDrop={(e) => {
                          e.preventDefault(); e.stopPropagation();
                          setIsDraggingOver(false);
                          const dropped = e.dataTransfer?.files;
                          if (dropped && dropped.length > 0) {
                            handleUploadClientFiles(dropped);
                          }
                        }}
                        className={`relative rounded-lg transition-all ${isDraggingOver ? 'ring-2 ring-[#0077B6] ring-offset-2 bg-blue-50/40' : ''}`}
                        data-testid="client-files-dropzone"
                      >
                        {isDraggingOver && (
                          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/80 backdrop-blur-sm">
                            <div className="flex items-center gap-3 text-[#0077B6] font-semibold">
                              <Upload className="h-6 w-6" />
                              Drop files to upload to {selectedFolder.client_name}
                            </div>
                          </div>
                        )}
                        {folderFiles.filter(f => !f.is_merged_package).length === 0 ? (
                          <div className="py-12 text-center text-gray-500">
                            <FileText className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                            <p>No files in this folder</p>
                            <p className="text-sm">Drag and drop files here, or use the Upload Files button</p>
                          </div>
                        ) : (
                          <ClientDocsBoard
                            apiUrl={API_URL}
                            folderId={selectedFolder.id}
                            files={folderFiles}
                            onRefresh={() => fetchFolderFiles(selectedFolder.id)}
                            onRename={handleRenameClientFile}
                            onDelete={handleDeleteClientFile}
                            onMerged={() => { setMergeRefreshKey(k => k + 1); fetchFolderFiles(selectedFolder.id); }}
                          />
                        )}
                        <MergeFilesPanel
                          apiUrl={API_URL}
                          folderId={selectedFolder.id}
                          folder={selectedFolder}
                          agentName={agent?.name}
                          selectedFileIds={selectedFileIds}
                          onClearSelection={clearFileSelection}
                          refreshKey={mergeRefreshKey}
                        />
                      </div>
                    ) : (
                      <div className="py-12 text-center text-gray-400">
                        <ChevronRight className="h-12 w-12 mx-auto mb-2" />
                        <p>Select a client folder from the left</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Regulatory Forms Tab — annual per-agent compliance PDFs */}
          {activeTab === 'regulatory' && <RegulatoryFormsTab agent={agent} apiUrl={API_URL} />}

          {activeTab === 'my-documents' && <AgentDocumentsTab agentId={agent.id} apiUrl={API_URL} />}
          {activeTab === 'translation' && <TranslationPanel mode="agent" agent={agent} apiUrl={API_URL} />}
          {activeTab === 'memos' && <AgentMemosPanel agent={agent} apiUrl={API_URL} />}

          {/* Common Files Tab */}
          {activeTab === 'common' && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <CardTitle className="flex items-center gap-2">
                      <Building className="h-5 w-5 text-[#0077B6]" />
                      Common Files
                    </CardTitle>
                    <div className="flex items-center gap-3">
                      <div className="flex rounded-lg bg-gray-100 p-1">
                        {['all', 'company', 'mortgage', 'insurance_investments'].map(cat => (
                          <button
                            key={cat}
                            onClick={() => setCommonFilesCategory(cat)}
                            className={`px-3 py-1 text-sm rounded-md transition-colors ${
                              commonFilesCategory === cat ? 'bg-white shadow text-[#0077B6] font-medium' : 'text-gray-600 hover:text-gray-800'
                            }`}
                          >
                            {cat === 'all' ? 'All' : 
                             cat === 'company' ? 'Company' : 
                             cat === 'mortgage' ? 'Mortgage' : 'Insurance & Investments'}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Files are filtered based on your licenses. Contact admin to upload new files.
                  </p>
                </CardHeader>
                <CardContent>
                  {commonFiles.length === 0 ? (
                    <div className="py-12 text-center text-gray-500">
                      <Briefcase className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                      <p>No files available in this category</p>
                      <p className="text-sm">Files you have access to will appear here</p>
                    </div>
                  ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {commonFiles.map(file => (
                        <div key={file.id} className="border rounded-lg p-4 hover:border-[#0077B6] hover:shadow-md transition-all group">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              {getFileIcon(file.file_type)}
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                file.category === 'company' ? 'bg-blue-100 text-blue-700' :
                                file.category === 'mortgage' ? 'bg-green-100 text-green-700' :
                                'bg-purple-100 text-purple-700'
                              }`}>
                                {file.category === 'company' ? 'Company' : 
                                 file.category === 'mortgage' ? 'Mortgage' : 'Insurance & Investments'}
                              </span>
                            </div>
                          </div>
                          <h4 className="font-medium text-gray-800 mb-1 truncate">{file.name}</h4>
                          <p className="text-xs text-gray-500 mb-3 truncate">{file.filename}</p>
                          {file.description && <p className="text-sm text-gray-600 mb-3 line-clamp-2">{file.description}</p>}
                          <div className="flex items-center justify-between text-xs text-gray-400">
                            <span>{formatFileSize(file.file_size)}</span>
                            <Button variant="outline" size="sm" asChild>
                              <a href={`${API_URL}/api/common-files/download/${file.id}`} onClick={openAuthed} target="_blank" rel="noopener noreferrer">
                                <Download className="h-3 w-3 mr-1" />Download
                              </a>
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* Task Modal */}
        {showTaskModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>{editingTask ? 'Edit Task' : 'New Task'}</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowTaskModal(false)}><X className="h-5 w-5" /></Button>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSaveTask} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Title *</label>
                    <input
                      type="text"
                      value={taskForm.title}
                      onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                      required
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500"
                      placeholder="Task title..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Description</label>
                    <textarea
                      value={taskForm.description}
                      onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                      rows={2}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500"
                      placeholder="Task details... (paste a link, e.g. https://zoom.us/j/123 — it becomes clickable)"
                    />
                    <p className="text-xs text-gray-400 mt-1">Tip: paste a full link (https://…) and it will be clickable for recipients.</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">Due Date</label>
                      <input
                        type="date"
                        value={taskForm.due_date}
                        onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Due Time</label>
                      <input
                        type="time"
                        value={taskForm.due_time}
                        onChange={(e) => setTaskForm({ ...taskForm, due_time: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">Priority</label>
                      <select
                        value={taskForm.priority}
                        onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Category</label>
                      <select
                        value={taskForm.category}
                        onChange={(e) => setTaskForm({ ...taskForm, category: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-rose-500"
                      >
                        <option value="general">General</option>
                        <option value="client">Client</option>
                        <option value="follow_up">Follow Up</option>
                        <option value="meeting">Meeting</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button type="button" variant="outline" onClick={() => setShowTaskModal(false)} className="flex-1">Cancel</Button>
                    <Button type="submit" className="flex-1 bg-rose-500 hover:bg-rose-600">{editingTask ? 'Update' : 'Create'} Task</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Event Modal */}
        {showEventModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>{editingEvent ? 'Edit Event' : 'New Event'}</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowEventModal(false)}><X className="h-5 w-5" /></Button>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSaveEvent} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Title *</label>
                    <input
                      type="text"
                      value={eventForm.title}
                      onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                      required
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="Event title..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Description</label>
                    <textarea
                      value={eventForm.description}
                      onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                      rows={2}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="Event details... (paste a link, e.g. https://zoom.us/j/123 — it becomes clickable)"
                    />
                    <p className="text-xs text-gray-400 mt-1">Tip: paste a full link (https://…) and it will be clickable for recipients.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Date *</label>
                    <input
                      type="date"
                      value={eventForm.start_date}
                      onChange={(e) => setEventForm({ ...eventForm, start_date: e.target.value })}
                      required
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      id="all_day"
                      checked={eventForm.all_day}
                      onChange={(e) => setEventForm({ ...eventForm, all_day: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                    <label htmlFor="all_day" className="text-sm">All day event</label>
                  </div>
                  {!eventForm.all_day && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium mb-1">Start Time</label>
                        <input
                          type="time"
                          value={eventForm.start_time}
                          onChange={(e) => setEventForm({ ...eventForm, start_time: e.target.value })}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">End Time</label>
                        <input
                          type="time"
                          value={eventForm.end_time}
                          onChange={(e) => setEventForm({ ...eventForm, end_time: e.target.value })}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium mb-1">Color</label>
                    <div className="flex gap-2">
                      {['#0077B6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'].map(color => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setEventForm({ ...eventForm, color })}
                          className={`w-8 h-8 rounded-full ${eventForm.color === color ? 'ring-2 ring-offset-2 ring-gray-400' : ''}`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <Button type="button" variant="outline" onClick={() => setShowEventModal(false)} className="flex-1">Cancel</Button>
                    <Button type="submit" className="flex-1 bg-indigo-500 hover:bg-indigo-600">{editingEvent ? 'Update' : 'Create'} Event</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Company Event (read-only) View Modal */}
        {companyEventView && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setCompanyEventView(null)} data-testid="company-event-view-modal">
            <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Lock className="h-4 w-4 text-indigo-600" />
                  <span>{companyEventView.title}</span>
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setCompanyEventView(null)} data-testid="close-company-event-view"><X className="h-5 w-5" /></Button>
              </CardHeader>
              <CardContent className="space-y-3">
                <span className="inline-flex items-center gap-1 bg-indigo-100 text-indigo-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                  <Building className="h-3 w-3" /> Company event
                </span>
                <p className="text-sm text-gray-600 flex items-center gap-1.5">
                  <CalendarDays className="h-4 w-4 text-gray-400" />
                  {companyEventView.start_date}{!companyEventView.all_day && companyEventView.start_time ? ` at ${companyEventView.start_time}` : ''}
                </p>
                {companyEventView.autopilot?.graphic_path && (
                  <img src={`${API_URL}${companyEventView.autopilot.graphic_path}`} alt={companyEventView.title} className="w-full rounded-lg border" data-testid="company-event-graphic" />
                )}
                {companyEventView.description && (
                  <div className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3" data-testid="company-event-description">
                    <LinkifiedText text={companyEventView.description} />
                  </div>
                )}
                <p className="text-xs text-gray-400">{companyEventView.source === 'autopilot' ? 'Placed automatically by the Utmost Calendar Autopilot.' : 'Added by Utmost Financial admin — read-only from your portal.'}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {showNewFolderModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>New Client Folder</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowNewFolderModal(false)}><X className="h-5 w-5" /></Button>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateFolder} className="space-y-4">
                  <div><label className="block text-sm font-medium mb-1">Client Name *</label><input type="text" value={folderForm.client_name} onChange={(e) => setFolderForm({...folderForm, client_name: e.target.value})} required className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]" placeholder="John Smith" /></div>
                  <div><label className="block text-sm font-medium mb-1">Client Email</label><input type="email" value={folderForm.client_email} onChange={(e) => setFolderForm({...folderForm, client_email: e.target.value})} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]" placeholder="john@example.com" /></div>
                  <div><label className="block text-sm font-medium mb-1">Client Phone</label><input type="tel" value={folderForm.client_phone} onChange={(e) => setFolderForm({...folderForm, client_phone: e.target.value})} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]" placeholder="204-555-1234" /></div>
                  <div><label className="block text-sm font-medium mb-1">Notes</label><textarea value={folderForm.notes} onChange={(e) => setFolderForm({...folderForm, notes: e.target.value})} rows={3} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]" placeholder="Additional notes about this client..." /></div>
                  <div className="flex gap-3 pt-2">
                    <Button type="button" variant="outline" onClick={() => setShowNewFolderModal(false)} className="flex-1">Cancel</Button>
                    <Button type="submit" disabled={folderLoading} className="flex-1 bg-[#0077B6]">{folderLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Folder'}</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Edit Folder Modal */}
        {editingFolder && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Edit Client Folder</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setEditingFolder(null)}><X className="h-5 w-5" /></Button>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleUpdateFolder} className="space-y-4">
                  <div><label className="block text-sm font-medium mb-1">Client Name *</label><input type="text" value={folderForm.client_name} onChange={(e) => setFolderForm({...folderForm, client_name: e.target.value})} required className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]" /></div>
                  <div><label className="block text-sm font-medium mb-1">Client Email</label><input type="email" value={folderForm.client_email} onChange={(e) => setFolderForm({...folderForm, client_email: e.target.value})} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]" /></div>
                  <div><label className="block text-sm font-medium mb-1">Client Phone</label><input type="tel" value={folderForm.client_phone} onChange={(e) => setFolderForm({...folderForm, client_phone: e.target.value})} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]" /></div>
                  <div><label className="block text-sm font-medium mb-1">Notes</label><textarea value={folderForm.notes} onChange={(e) => setFolderForm({...folderForm, notes: e.target.value})} rows={3} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0077B6]" /></div>
                  <div className="flex gap-3 pt-2">
                    <Button type="button" variant="outline" onClick={() => setEditingFolder(null)} className="flex-1">Cancel</Button>
                    <Button type="submit" disabled={folderLoading} className="flex-1 bg-[#0077B6]">{folderLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Changes'}</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    );
  }

  // Login Form View
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#003D5C] to-[#0077B6] flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="h-8 w-8 text-[#003D5C]" />
          </div>
          <CardTitle className="text-2xl">Agent Portal</CardTitle>
          <p className="text-gray-600 mt-2">Sign in to access your dashboard</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            {error && <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">{error}</div>}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="your.email@example.com" className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent" data-testid="login-email-input" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="Enter your password" className="w-full pl-10 pr-12 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent" data-testid="login-password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-[#0077B6] hover:bg-[#005A8C]" data-testid="login-submit-btn">
              {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Signing in...</> : 'Sign In'}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <button onClick={() => setShowForgotPassword(true)} className="text-sm text-[#0077B6] hover:underline" data-testid="forgot-password-link">Forgot your password?</button>
          </div>
          <div className="mt-4 pt-4 border-t text-center">
            <a href="/" className="text-sm text-gray-500 hover:text-[#0077B6]">← Back to Main Website</a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AgentLoginPage;
